/**
 * Imperatively trigger a browser file download by injecting a hidden anchor.
 * For blob downloads, use `downloadBlob` instead — it manages the object URL
 * lifecycle (createObjectURL/revokeObjectURL) for you.
 */
export const triggerDownload = (url: string, fileName: string): void => {
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

/**
 * Download a Blob as a file. Wraps `URL.createObjectURL` + `triggerDownload` +
 * `URL.revokeObjectURL` so callers can't leak the object URL.
 */
export const downloadBlob = (blob: Blob, fileName: string): void => {
  const objectUrl = window.URL.createObjectURL(blob);
  try {
    triggerDownload(objectUrl, fileName);
  } finally {
    window.URL.revokeObjectURL(objectUrl);
  }
};

/**
 * Download a remote (typically cross-origin, CDN-hosted) file under a chosen
 * filename by fetching the bytes into a blob first, then saving that blob.
 *
 * Why not point an `<a download>` straight at the URL: browsers ignore the
 * anchor's `download` attribute for cross-origin URLs, so a direct link only
 * downloads when the server sends `Content-Disposition: attachment`. Many of
 * our older CDN-hosted images lack that header, so a direct link just opens the
 * image inline (a new tab) instead of downloading. Routing the bytes through a
 * same-origin `blob:` URL makes `download` authoritative again and lets us
 * control the filename — regardless of the object's response headers.
 *
 * Requires the host to allow CORS (all current order-image hosts do). If the
 * fetch fails — a CORS-blocked host, a network error, or a non-OK response — we
 * fall back to the plain direct-link download, so behavior is never worse than
 * a bare `triggerDownload`.
 *
 * `cache: 'reload'` is essential, not an optimization: the image viewer loads
 * each photo with a plain `<img>` (no `crossOrigin`) first, which caches a
 * no-CORS response with no `Access-Control-Allow-Origin`. A default-cache
 * `fetch(mode:'cors')` of the same URL reuses that entry, fails the CORS check,
 * and degrades to the new-tab fallback. Forcing a fresh network request makes
 * the fetch send `Origin` and receive `ACAO`, so the download succeeds
 * regardless of what the `<img>` cached.
 */
export const downloadRemoteFile = async (
  url: string,
  fileName: string
): Promise<void> => {
  try {
    const response = await fetch(url, { mode: 'cors', cache: 'reload' });
    if (!response.ok) throw new Error(`Download request failed: ${response.status}`);
    downloadBlob(await response.blob(), fileName);
  } catch {
    triggerDownload(url, fileName);
  }
};
